import axios from 'axios';
import { ticketDead } from '../ticketWords';

/** How far a citation has been checked against the document's own text. */
export type CitationStatus = 'CONFIRMED' | 'VERIFIED' | 'UNVERIFIED' | 'MISSING';

export interface SourceCitation {
  page: number | null;
  section: string | null;
  clause: string | null;
  quote: string | null;
  status: CitationStatus;
  /** FIELD: the passage states this value. RECORD: it states the record the value belongs to. */
  scope: 'FIELD' | 'RECORD';
  origin: 'EXTRACTION' | 'LOCATOR' | 'REVIEWER' | null;
  /** Set when a reviewer pointed the value at this page or clause. */
  reviewed_by?: string | null;
  reviewed_at?: string | null;
}

export interface AgreementReviewRecord {
  job_id: string;
  document_name: string | null;
  document_type: string | null;
  created_at: string | null;
  /** The record under review: reviewer corrections applied when there are any. */
  record: Record<string, unknown>;
  /** What extraction first read, present only when a reviewer corrected the record. */
  extracted_record: Record<string, unknown> | null;
  /** One citation per stated value, keyed by JSON pointer into `record`. */
  citations: Record<string, SourceCitation>;
  coverage: { stated: number; verified: number; unverified: number; missing: number };
  document: { available: boolean; page_count: number | null };
  sources_located: { located_at?: string; model?: string };
  approval_status: string;
  /** F-PCP-039: the template's validation of the read (errors per field), when the document was read with one. */
  template_validation?: { status?: string; error_count?: number; reason?: string; errors?: Record<string, Array<{ rule: string; message: string; source?: string }>> } | null;
  template_version?: number | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  booking_status: string | null;
  last_booking_error: string | null;
  oms_import_status: string | null;
  oms_import_result: Record<string, unknown> | null;
  linked_security_id: number | null;
  linked_loan_record_id: number | null;
  linked_credit_deal_id: number | null;
  linked_portfolio_id: number | null;
}

export interface AcquisitionTicket {
  id: number;
  status: string;
  eventType: string | null;
  portfolioId: number | null;
}

export const getAgreementReviewRecord = async (jobId: string): Promise<AgreementReviewRecord> => {
  const response = await axios.get<AgreementReviewRecord>(`/api/v1/jobs/${encodeURIComponent(jobId)}/review-record`);
  return response.data;
};

export const locateAgreementSources = async (jobId: string): Promise<AgreementReviewRecord> => {
  const response = await axios.post<AgreementReviewRecord>(
    `/api/v1/jobs/${encodeURIComponent(jobId)}/review-record/locate-sources`,
  );
  return response.data;
};

/** Record where the reviewer says a value is stated. The value itself is not changed. */
export const confirmAgreementSource = async (
  jobId: string, pointer: string, page: number | null, clause: string | null,
): Promise<AgreementReviewRecord> => {
  const response = await axios.post<AgreementReviewRecord>(
    `/api/v1/jobs/${encodeURIComponent(jobId)}/review-record/sources`, { pointer, page, clause },
  );
  return response.data;
};

export const getAgreementDocument = async (jobId: string): Promise<Blob> => {
  const response = await axios.get(`/api/v1/jobs/${encodeURIComponent(jobId)}/document`, { responseType: 'blob' });
  return response.data as Blob;
};

/** The acquisition ticket the import created for this security, newest first. */
export const getAcquisitionTicket = async (securityId: number): Promise<AcquisitionTicket | null> => {
  const response = await axios.get(`/api/trade-transactions/security/${securityId}`);
  const rows: AcquisitionTicket[] = Array.isArray(response.data) ? response.data : response.data?.content || [];
  return rows
    .filter(row => String(row.eventType || '').toUpperCase() === 'ACQUIRE' && !ticketDead(row.status))
    .sort((left, right) => right.id - left.id)[0] || null;
};

/** The private credit agreement a facility was booked from, newest first. Empty when it was entered by hand. */
export const findAgreementJobForLoan = async (loanRecordId: number): Promise<string | null> => {
  // The loan id is only unique within a lane, so the document type is part of the question.
  const response = await axios.get('/api/v1/jobs', {
    params: { documentType: 'CREDIT_AGREEMENT', linkedLoanRecordId: loanRecordId, status: 'COMPLETED' },
  });
  const jobs: { job_id: string }[] = response.data?.jobs || [];
  return jobs[0]?.job_id || null;
};

export const reviewErrorMessage = (failure: unknown, fallback: string): string => {
  const data = (failure as { response?: { data?: { detail?: string; error?: string; message?: string } } })?.response?.data;
  return data?.detail || data?.message || data?.error || fallback;
};
